'use client'

import { usePricingRules } from '@/components/providers/PricingRulesProvider'
import { OrderField } from '@/components/sections/order/order-field.client'
import {
  type CreatorOfferingView,
  type CreatorPick,
  footageFor,
  formatMinutes,
} from '@/lib/creator'
import { formatPLN } from '@/lib/tiers'
import { META } from '@/lib/typography'
import { cn } from '@/lib/utils'

/**
 * "Ile masz surówki?" — spec §8's raw-footage declaration (G7), as a slider.
 *
 * The number matters because editing time scales with what has to be WATCHED,
 * not with what comes out: a ten-minute reel cut from twenty minutes of tape
 * and one cut from four hours are the same delivery and completely different
 * jobs. Spec §8 prices the difference rather than refusing it — 2 zł net a
 * minute over the allowance, charged up front.
 *
 * ── WHY A SLIDER REPLACED THE BOX ────────────────────────────────────────
 * The allowance and the fee are the point of the control, and both are live.
 * A client who can see "limit 75 min · 115 min ponad · +230 zł" while they
 * ANSWER can decide to send less tape, buy a longer band, or accept the fee —
 * which is a decision. A number box made that a typing exercise: you had to
 * already know your answer to discover what it cost. Dragging past the limit
 * shows the moment the fee starts, which is the one thing the box could not do.
 *
 * The readout stays a real number input, so an exact figure — or one past the
 * slider's ceiling — is still one keystroke away. The slider is the fast path,
 * not a cage.
 */
export function FootageField({
  offering,
  pick,
  lineId,
  problem,
  onPatch,
}: {
  offering: CreatorOfferingView
  pick: CreatorPick
  /** Scopes the control id: the same offering can be in the order twice. */
  lineId: string
  problem?: string | null
  onPatch: (next: Partial<CreatorPick>) => void
}) {
  const footage = footageFor(offering, pick, usePricingRules())
  if (!footage) return null

  const over = Math.max(0, footage.declared - footage.limit)
  // Enough room to run well past the allowance — that is the stretch a person
  // needs to SEE, because it is where the fee lives. Rounded to whole hours so
  // the track's end lands on a number worth printing, and never below what has
  // already been declared: a ceiling under the current value would drag it down.
  const cap = Math.max(240, Math.ceil((footage.limit * 3) / 60) * 60, footage.declared)
  const answered = footage.declared > 0

  return (
    <OrderField
      wide
      htmlFor={`footage-${lineId}`}
      label="Ile minut surówki wysyłasz?"
      problem={problem}
      hint={
        !answered
          ? // "Mieści się w limicie" was what an UNANSWERED control said, which
            // is a reassurance about a declaration nobody has made.
            'Przesuń suwak albo wpisz liczbę — od niej zależy, czy mieścisz się w limicie.'
          : over > 0
            ? `${over} min ponad limit — dopłata ${formatPLN(footage.overNet / 100)} netto, doliczona do tego zamówienia.`
            : 'Mieści się w limicie. Jeśli surówki będzie więcej, niż deklarujesz, montażysta zgłosi to i doliczymy różnicę przed startem pracy.'
      }
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <span className="flex items-baseline gap-2">
          <input
            id={`footage-${lineId}`}
            type="number"
            inputMode="numeric"
            min={0}
            max={1440}
            value={pick.footageMinutes}
            onChange={(event) => onPatch({ footageMinutes: event.target.value })}
            placeholder="0"
            aria-describedby={`footage-limit-${lineId}`}
            className="w-20 rounded-xl border border-white/15 bg-black/40 px-2 py-1.5 text-center text-[1.05rem] font-semibold text-white outline-none transition focus:border-white/40 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
          />
          <span className="text-[0.95rem] font-semibold text-white/70">min</span>
          {answered ? (
            <span className={cn(META, 'text-white/55')}>({formatMinutes(footage.declared)})</span>
          ) : null}
        </span>
        <span
          id={`footage-limit-${lineId}`}
          className={cn(
            META,
            'font-semibold',
            over > 0 ? 'text-[oklch(0.85_0.15_75)]' : 'text-white/55',
          )}
        >
          w cenie: {footage.limit} min
        </span>
      </div>

      <input
        type="range"
        aria-label="Minuty surówki"
        min={0}
        max={cap}
        step={15}
        value={Math.min(cap, footage.declared)}
        onChange={(event) => onPatch({ footageMinutes: event.target.value })}
        aria-valuetext={
          answered
            ? `${footage.declared} minut surówki, w cenie ${footage.limit} minut`
            : 'nie podano'
        }
        className="mt-3 w-full accent-white"
      />

      {/* The allowance marked on the track itself — the number under the
          slider is where the fee begins, so it belongs on the ruler and not
          only in a sentence. */}
      <div className={cn(META, 'mt-1 flex justify-between text-white/55')} aria-hidden>
        <span>0</span>
        <span>{cap} min</span>
      </div>
    </OrderField>
  )
}
